import React, {useCallback, useState} from 'react'
import {MembersPageTemplate} from '../../Molecules/BaseMembersPages/MembersPageTemplate'
import {PageTitle} from '../../Molecules/BaseMembersPages/PageTitle'
import {MailAddressFormContent} from '../../Components/InputContentBlocks/MailAddressFormContent'
import {MessageForAction} from '../../Molecules/BaseMembersPages/MessageForAction'
import MemberRepo from '../../../Repo/MemberRepo'
import {MemberVerifyIdentityDTO} from '../../../DTO/MemberVerifyIdentityDTO'
import {CommonResponseStatus} from '../../../../Network/CommonResponse'
import {NavigatorForMemberPage} from '../../../Navigator/NavigatorForMemberPage'
import {validateMemberMailInput} from '../../../Utils/Validations/ValidateMembersMailInput'
import {animateScroll} from 'react-scroll'
import {useMBMemberError} from '../../Hooks/UseMBMemberError'
import {MBMemberError} from '../../../Common/MBMemberError'
import {PIMSubmitButton} from '../../Atoms/Buttons/Bases/PIMSubmitButton'
import {PIMPageActions} from '../../Atoms/Layouts/PIMPageActions'
import {InputErrorSummary} from '../../Molecules/ErrorsAndAlert/ErrorSummary/InputErrorSummary'
import {useTranslation} from 'react-i18next'

type ErrorState = {
  hasErrors: boolean
  inputErrors: string[]
  errorMessages: string[]
}

type MembersVerifyIdentityProps = {
  navigator: NavigatorForMemberPage
  memberRepo: MemberRepo
}
export const MembersVerifyIdentityPage: React.VFC<MembersVerifyIdentityProps> = (props) => {
  const {t} = useTranslation('member')
  const [mail, setMail] = useState('')
  const [mailValidateError, setMailValidateError] = useState<ErrorState>({
    hasErrors: false,
    inputErrors: [],
    errorMessages: [],
  })
  const [processing, setProcessing] = useState<boolean>(false)

  const throwMBMemberError = useMBMemberError()

  const handleSendButtonClick = useCallback(async (): Promise<void> => {
    setProcessing(true)
    setMailValidateError({
      hasErrors: false,
      inputErrors: [],
      errorMessages: [],
    })

    const validationResult = validateMemberMailInput({mail: mail}, true)

    if (validationResult.hasError) {
      setMailValidateError({
        hasErrors: validationResult.hasError,
        inputErrors: validationResult.errors.mail,
        errorMessages: [],
      })
      setProcessing(false)
      animateScroll.scrollToTop()
      return
    }

    try {
      const postResult = await props.memberRepo.postVerifyIdentityMail(
        new MemberVerifyIdentityDTO(mail),
      )
      switch (postResult.status) {
        case CommonResponseStatus.Ok:
          props.navigator.goToMembersVerifiedIdentityPage()
          break
        case CommonResponseStatus.BadRequest:
          setProcessing(false)
          setMailValidateError({
            hasErrors: true,
            inputErrors: [],
            errorMessages: postResult.errorMessageList,
          })
          break
        case CommonResponseStatus.InternalServerError:
          // noinspection ExceptionCaughtLocallyJS
          throw new MBMemberError('ServerError')
        default:
          // noinspection ExceptionCaughtLocallyJS
          throw new MBMemberError('ServerError')
      }
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : 'SystemError'
      throwMBMemberError(errorMessage)
    }
  }, [mail])

  return (
    <MembersPageTemplate>
      <PageTitle>{'パスワード再設定用メールアドレス入力'}</PageTitle>

      <InputErrorSummary display={mailValidateError.hasErrors}/>

      <MailAddressFormContent
        title={'本人確認メールアドレス入力'}
        topMessage={t('パスワード再設定用メールアドレス入力.本人確認メールアドレス入力.説明')}
        note={[t('パスワード再設定用メールアドレス入力.本人確認メールアドレス入力.注記 メール受信設定')]}
        inputLabel={t('パスワード再設定用メールアドレス入力.本人確認メールアドレス入力.ラベル メールアドレス')}
        inputValue={mail}
        hasErrors={mailValidateError.hasErrors}
        errorMessages={[...mailValidateError.inputErrors, ...mailValidateError.errorMessages]}
        onMailAddressChange={(mailAddress: string): void => {
          setMail(mailAddress)
        }}
        onMailAddressBlur={(mailAddress: string): void => {
          setMail(mailAddress)
        }}
      />

      <MessageForAction>
        上記の内容に間違いがなければ<br/>
        『送信する』ボタンを押してください。
      </MessageForAction>

      <PIMPageActions>
        <PIMSubmitButton
          testId={'send-button'}
          disabled={processing}
          onClick={(): Promise<void> => handleSendButtonClick()}
        >
          送信する
        </PIMSubmitButton>
      </PIMPageActions>

    </MembersPageTemplate>
  )
}
